import { Component, inject, OnDestroy, OnInit, PLATFORM_ID, ViewChild } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { UssdSimulatorStore, UssdSessionState, UssdTurn } from '../../../../services/ussd/ussd-simulator.store';
import { UssdService } from '../../../../services/ussd/ussd.service';
import { UssdApplicationsService } from '../../../../services/ussd/ussd-applications.service';
import { UserProfileService } from '../../../../services/user/user-profile.service';
import { SettingsService } from '../../../../services/settings/settings.service';
import { ToastService } from '../../../../services/toast/toast.service';
import { PhoneFrameComponent } from '../phone-frame/phone-frame.component';
import { DialerScreenComponent } from '../dialer-screen/dialer-screen.component';
import { UssdDialogComponent } from '../ussd-dialog/ussd-dialog.component';
import { PhoneInputComponent, PhoneInputValue } from '../phone-input/phone-input.component';
import { StatusComponent } from '../../../../components/ui/status/status.component';
import {
  COUNTRIES,
  CountryInfo,
  PhoneScreen,
  UssdApplication,
  UssdOperator,
  UssdServiceResponse,
} from '../../../../types/ussd.models';
import { IUserOrganization } from '../../../../types/settingsDataModule';
import { csvFileName, downloadCsv, toCsv } from '../../../../../utils/csv';
import {
  countryByCode,
  countryName,
  environmentLabel,
  formatDuration,
  StatusView,
  ussdScreenCheck,
  UssdScreenCheck,
} from '../ussd-display';

/** Where the last test number lives between visits (this browser only). */
const NUMBER_KEY = 'bk.ussd.simulator.number';

interface TurnRow {
  id: number;
  kind: UssdTurn['kind'];
  label: string;
  input: string;
  time: string;
  pending: boolean;
  latency: string;
  responseText: string;
  responseType: string;
  error: string | null;
  check: UssdScreenCheck | null;
  requestJson: string;
  responseJson: string;
}

@Component({
  selector: 'app-simulator',
  standalone: true,
  imports: [
    RouterLink,
    MatTooltipModule,
    PhoneFrameComponent,
    DialerScreenComponent,
    UssdDialogComponent,
    PhoneInputComponent,
    StatusComponent,
  ],
  templateUrl: './simulator.component.html',
  styleUrl: './simulator.component.css',
})
export class SimulatorComponent implements OnInit, OnDestroy {
  store = inject(UssdSimulatorStore);
  private ussdService = inject(UssdService);
  private applicationsService = inject(UssdApplicationsService);
  private userService = inject(UserProfileService);
  private settingsService = inject(SettingsService);
  private toast = inject(ToastService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  @ViewChild(DialerScreenComponent) private dialer?: DialerScreenComponent;

  readonly countries = COUNTRIES;
  readonly environmentLabel = environmentLabel;

  screen: PhoneScreen = 'dialer';
  phoneNumber: string = '';
  dialedCode: string = '';
  sessionId: string | null = null;
  ussdResponse: UssdServiceResponse | null = null;
  isLoading: boolean = false;
  error: string | null = null;
  selectedCountry: CountryInfo = this.store.selectedCountry;
  operator = '';
  sessionState: UssdSessionState = 'idle';
  sessionStartedAt: Date | null = null;
  turns: TurnRow[] = [];
  numberValid = false;

  operators: UssdOperator[] = [];
  operatorsState: 'idle' | 'loading' | 'ready' | 'none' = 'idle';

  apps: UssdApplication[] = [];
  appsState: 'loading' | 'ready' | 'error' = 'loading';

  /** Turn ids whose raw request/response is open. */
  openTurns = new Set<number>();

  private subs: Subscription[] = [];
  /** Operators load once the saved number and link have set the country. */
  private initialised = false;
  private operatorsFor = '';
  /** Set by ?country= or a saved number, so the org default does not override it. */
  private countryFromLink = false;
  /** The org's IAM country was applied; the first app's country must not replace it. */
  private orgCountryApplied = false;

  // ── Derived ──

  get ussdMessage(): string {
    return this.ussdResponse?.serviceData || '';
  }

  get isSessionEnd(): boolean {
    return this.ussdResponse?.serviceRequestType === 'Release' || this.ussdResponse?.serviceRequestType === 'Timeout';
  }

  get isError(): boolean {
    return !!this.error || this.sessionState === 'failed';
  }

  get carrier(): string {
    if (this.operator) {
      return this.operators.find((o) => o.code === this.operator)?.name || this.operator;
    }
    return 'Test network';
  }

  get msisdn(): string {
    return this.phoneNumber ? this.store.msisdn : '';
  }

  get sessionView(): StatusView {
    switch (this.sessionState) {
      case 'dialing':
        return { text: 'Dialling', status: 'active' };
      case 'open':
        return { text: 'Waiting for your reply', status: 'active' };
      case 'waiting':
        return { text: 'Waiting for the application', status: 'active' };
      case 'ended':
        return { text: 'Ended', status: 'cancelled' };
      case 'failed':
        return { text: 'Failed', status: 'failed' };
      default:
        return { text: 'No session', status: 'cancelled' };
    }
  }

  get sessionLive(): boolean {
    return this.store.isSessionLive;
  }

  get dialHint(): string {
    if (!this.phoneNumber) return 'Enter a test number first';
    const first = this.apps.find((a) => a.serviceCode);
    return first ? `Try ${first.serviceCode}, ${first.name}` : 'Dial a USSD code, such as *123#';
  }

  /** One line for screen readers on the newest finished turn; the handset announces the menu itself. */
  get transcriptStatus(): string {
    const latest = this.turns[0];
    if (!latest || latest.pending) return '';
    if (latest.error) return `${latest.label}: ${latest.error}`;
    return latest.latency ? `${latest.label}, answered in ${latest.latency}` : latest.label;
  }

  get roundTrip(): string {
    const done = this.turns.filter((t) => t.kind !== 'Release' && !t.pending);
    return done.length ? `${done.length} ${done.length === 1 ? 'step' : 'steps'}` : '';
  }

  /** The dialled code's application, when it is one of the organisation's. */
  get dialedApp(): UssdApplication | null {
    const code = (this.store.currentServiceCode || this.dialedCode || '').trim();
    return code ? this.apps.find((a) => a.serviceCode === code) ?? null : null;
  }

  get countryMismatch(): string {
    const app = this.dialedApp;
    if (!app?.countryCode || app.countryCode === this.selectedCountry.code) return '';
    return countryName(app.countryCode, app.countryName);
  }

  // ── Lifecycle ──

  ngOnInit(): void {
    this.subs.push(
      this.store.screen$.subscribe((s) => (this.screen = s === 'ussd' ? 'ussd' : 'dialer')),
      this.store.phoneNumber$.subscribe((n) => (this.phoneNumber = n)),
      this.store.dialedCode$.subscribe((d) => (this.dialedCode = d)),
      this.store.sessionId$.subscribe((id) => (this.sessionId = id)),
      this.store.ussdResponse$.subscribe((r) => (this.ussdResponse = r)),
      this.store.isLoading$.subscribe((l) => (this.isLoading = l)),
      this.store.error$.subscribe((e) => (this.error = e)),
      this.store.selectedCountry$.subscribe((c) => {
        this.selectedCountry = c;
        if (this.initialised) this.loadOperators(c.code);
      }),
      this.store.operator$.subscribe((o) => (this.operator = o)),
      this.store.sessionState$.subscribe((s) => (this.sessionState = s)),
      this.store.sessionStartedAt$.subscribe((d) => (this.sessionStartedAt = d)),
      this.store.transcript$.subscribe((turns) => (this.turns = turns.map((t) => this.toRow(t)).reverse()))
    );

    this.restoreNumber();
    this.applyLink();
    this.initialised = true;
    this.loadOperators(this.selectedCountry.code);
    this.loadApps();
    this.useOrganisationCountry();
  }

  ngOnDestroy(): void {
    // Leaving the page hangs up, so no session waits on the server for a timeout.
    this.store.hangUp();
    this.subs.forEach((s) => s.unsubscribe());
  }

  // ── Set-up ──

  /** ?code=*384*123#&country=BW from "Test in simulator". */
  private applyLink(): void {
    const params = this.route.snapshot.queryParamMap;
    const country = countryByCode(params.get('country'));
    const code = params.get('code');

    if (country) {
      this.countryFromLink = true;
      if (country.code !== this.store.selectedCountry.code) {
        // A number saved for another country would dial with the wrong prefix.
        const saved = this.readSaved();
        if (saved?.country === country.code) {
          this.store.setCountry(country);
          this.store.setPhoneNumber(saved.number);
        } else {
          this.store.setSelectedCountry(country);
        }
      } else {
        this.store.setCountry(country);
      }
    }
    if (code) {
      if (this.store.isSessionLive) this.store.hangUp();
      this.store.setDialedCode(code);
    }
    if ((country || code) && this.isBrowser) {
      // Drop the params so a refresh does not reset what the tester changed.
      this.router.navigate([], { relativeTo: this.route, queryParams: {}, replaceUrl: true });
    }
  }

  private restoreNumber(): void {
    if (this.store.phoneNumber) {
      this.countryFromLink = true;
      return;
    }
    const saved = this.readSaved();
    if (!saved) return;
    const country = countryByCode(saved.country);
    if (!country) return;
    this.countryFromLink = true;
    this.store.setCountry(country);
    this.store.setPhoneNumber(saved.number);
  }

  private readSaved(): { country: string; number: string } | null {
    if (!this.isBrowser) return null;
    try {
      const raw = window.localStorage.getItem(NUMBER_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as { country?: unknown; number?: unknown };
      return typeof parsed?.country === 'string' && typeof parsed?.number === 'string'
        ? { country: parsed.country, number: parsed.number }
        : null;
    } catch {
      return null;
    }
  }

  private saveNumber(): void {
    if (!this.isBrowser) return;
    try {
      if (this.phoneNumber) {
        window.localStorage.setItem(
          NUMBER_KEY,
          JSON.stringify({ country: this.selectedCountry.code, number: this.phoneNumber })
        );
      } else {
        window.localStorage.removeItem(NUMBER_KEY);
      }
    } catch {
      // Storage blocked: the number simply is not remembered.
    }
  }

  /** Default to the organisation's own country unless the tester or a link chose one. */
  private useOrganisationCountry(): void {
    if (!this.isBrowser || this.countryFromLink || this.store.countryChosen) return;
    this.subs.push(
      this.settingsService.getUserInstitutionData.subscribe((data: IUserOrganization | null) => {
        const country = countryByCode(data?.countryCode);
        if (country && !this.countryFromLink && !this.store.countryChosen && !this.phoneNumber) {
          this.orgCountryApplied = true;
          this.store.setCountry(country, false);
        }
      })
    );
    this.settingsService.fetchUserInstitution();
  }

  private loadApps(): void {
    if (!this.isBrowser) return;
    const organisationId = this.userService.organizationId ?? '';
    if (!organisationId) {
      this.appsState = 'ready';
      return;
    }
    this.appsState = 'loading';
    this.applicationsService
      .getApplications(organisationId, { page: 0, size: 50 })
      .pipe(take(1))
      .subscribe({
        next: (page) => {
          this.apps = (page.content ?? [])
            .filter((a) => !!a.serviceCode)
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
          this.appsState = 'ready';
          // Fall back to the country the organisation's applications are registered in,
          // unless the organisation's own country (IAM) has already been applied.
          if (!this.countryFromLink && !this.orgCountryApplied && !this.store.countryChosen && !this.phoneNumber) {
            const first = this.apps[0];
            const country = countryByCode(first?.ussdOrganisationProfile?.countryCode || first?.countryCode);
            if (country && country.code !== this.selectedCountry.code) this.store.setCountry(country, false);
          }
        },
        error: () => {
          this.apps = [];
          this.appsState = 'error';
        },
      });
  }

  private loadOperators(countryCode: string): void {
    if (!this.isBrowser || !countryCode || countryCode === this.operatorsFor) return;
    this.operatorsFor = countryCode;
    this.operators = [];
    this.operatorsState = 'loading';
    this.ussdService
      .getOperators(countryCode)
      .pipe(take(1))
      .subscribe({
        next: (list) => {
          if (countryCode !== this.operatorsFor) return;
          this.operators = list;
          this.operatorsState = list.length ? 'ready' : 'none';
          if (this.operator && !list.some((o) => o.code === this.operator)) this.store.setOperator('');
        },
        // 404 means the simulator has no networks for the country; detection by prefix still works.
        error: () => {
          if (countryCode !== this.operatorsFor) return;
          this.operators = [];
          this.operatorsState = 'none';
        },
      });
  }

  // ── Test number ──

  onNumberChange(value: PhoneInputValue): void {
    this.numberValid = value.isValid;
    this.store.setPhoneNumber(value.raw);
    if (this.error && value.raw) this.store.clearError();
    this.saveNumber();
  }

  onCountryChange(country: CountryInfo): void {
    if (this.store.isSessionLive) this.store.hangUp();
    this.store.setSelectedCountry(country);
  }

  onOperatorChange(event: Event): void {
    this.store.setOperator((event.target as HTMLSelectElement).value);
  }

  // ── Handset ──

  onCodeChange(code: string): void {
    this.store.setDialedCode(code);
  }

  dial(): void {
    if (!this.phoneNumber) {
      this.toast.info('Enter a test number, then dial again.');
      return;
    }
    this.store.handleCall();
  }

  quickDial(app: UssdApplication): void {
    if (!app.serviceCode) return;
    if (!this.phoneNumber) {
      this.store.setDialedCode(app.serviceCode);
      this.toast.info(`Enter a test number, then dial ${app.serviceCode}.`);
      return;
    }
    this.store.dial(app.serviceCode);
  }

  /** Home on the handset: hang up and clear the code, keep the test number. */
  goHome(): void {
    this.store.hangUp();
    this.store.setDialedCode('');
  }

  endSession(): void {
    this.store.handleUSSDCancel();
  }

  redial(): void {
    const code = this.store.currentServiceCode || this.dialedCode;
    if (code) this.store.dial(code);
  }

  // ── Transcript ──

  toggleTurn(id: number): void {
    const next = new Set(this.openTurns);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    this.openTurns = next;
  }

  clearTranscript(): void {
    this.openTurns = new Set();
    this.store.clearTranscript();
  }

  copySessionId(): void {
    const transcript = this.store.transcript;
    const id = this.sessionId || transcript[transcript.length - 1]?.sessionId;
    if (!id || !this.isBrowser || !navigator.clipboard) return;
    navigator.clipboard.writeText(String(id)).then(
      () => this.toast.success('Session ID copied.'),
      () => this.toast.error('The session ID could not be copied. Select it and copy it by hand.')
    );
  }

  downloadTranscript(): void {
    const turns = this.store.transcript;
    if (!turns.length) return;
    const csv = toCsv(turns, [
      { label: 'Session ID', value: (t) => t.sessionId },
      { label: 'Sent at', value: (t) => t.sentAt },
      { label: 'Request type', value: (t) => t.kind },
      { label: 'Input', value: (t) => t.input },
      { label: 'Response type', value: (t) => t.response?.serviceRequestType ?? '' },
      { label: 'Response', value: (t) => t.response?.serviceData ?? '' },
      { label: 'Characters', value: (t) => (t.response?.serviceData ? ussdScreenCheck(t.response.serviceData).length : '') },
      { label: 'Latency (ms)', value: (t) => t.latencyMs ?? '' },
      { label: 'Error', value: (t) => t.error ?? '' },
    ]);
    downloadCsv(csvFileName('ussd-simulator', this.store.currentServiceCode || 'session'), csv);
  }

  private toRow(t: UssdTurn): TurnRow {
    const text = t.response?.serviceData ?? '';
    const label =
      t.kind === 'New' ? `Dialled ${t.input}` : t.kind === 'Continue' ? `Replied ${t.input || '(empty)'}` : 'Hung up';
    return {
      id: t.id,
      kind: t.kind,
      label,
      input: t.input,
      time: t.sentAt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
      pending: t.pending,
      latency: t.latencyMs === null ? '' : formatDuration(t.latencyMs),
      responseText: text,
      responseType: t.response?.serviceRequestType ?? '',
      error: t.error,
      check: text && t.kind !== 'Release' ? ussdScreenCheck(text) : null,
      requestJson: JSON.stringify(t.request, null, 2),
      responseJson: t.response
        ? JSON.stringify(t.response, null, 2)
        : t.httpStatus !== null
          ? `HTTP ${t.httpStatus}`
          : t.pending
            ? 'Waiting…'
            : 'No response body',
    };
  }
}
